import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import authService from 'src/services/authService';

class BaseApiService {
    private _axiosInstance: AxiosInstance;

    constructor() {
        const defaultConfig: AxiosRequestConfig = {
            baseURL: this.getApiUrl(),
            timeout: 5 * 60 * 1000
        };

        this._axiosInstance = axios.create(defaultConfig);
        this._axiosInstance.interceptors.request.use((config) => {
            const authHeader = authService.getAuthHeader();
            Object.assign(config.headers, authHeader);
            return config;
        });
        this._axiosInstance.interceptors.response.use((response) => response, errorHandler);
    }

    protected getApiUrl() {
        return process.env.REACT_APP_API_URL;
    }

    protected get instance() {
        return this._axiosInstance;
    }

    protected async get<T, D = any>(url: string, config?: AxiosRequestConfig<D>) {
        return (await this._axiosInstance.get<T, AxiosResponse<T>, D>(`/lk/v3/${url}`, config)).data;
    }

    protected async post<T = void, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
        return (await this._axiosInstance.post<T, AxiosResponse<T>, D>(`/lk/v3/${url}`, data, config)).data;
    }

    protected async put<T = void, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
        return (await this._axiosInstance.put<T, AxiosResponse<T>, D>(`/lk/v3/${url}`, data, config)).data;
    }

    protected async delete<T = void, D = any>(url: string, config?: AxiosRequestConfig<D>) {
        return (await this._axiosInstance.delete<T, AxiosResponse<T>, D>(`/lk/v3/${url}`, config)).data;
    }

    protected async downloadFilePost<D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
        return await this._axiosInstance.post<Blob, AxiosResponse<Blob, D>, D>(`/lk/v3/${url}`, data, {
            responseType: 'blob',
            ...config
        });
    }

    /** helper to immitate delay */
    protected delay(ms?: number) {
        if (process.env.NODE_ENV === 'production') {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(undefined);
            }, ms ?? 1000);
        });
    }
}

const errorHandler = (error: AxiosError<any>) => {
    if (error instanceof axios.Cancel) {
        return;
    }

    if (error.response) {
        const { data, status } = error.response;
        if (status === 400 || status === 413) {
            if (data.message) {
                return Promise.reject(new Error(data.message));
            }
            return Promise.reject(new Error('Некорректный запрос'));
        } else if (status === 404) {
            return Promise.reject(new Error('Запрашиваемый ресурс не найден'));
        } else if (status === 401) {
            authService.removeData();
            window.location.reload();
            return Promise.reject(new Error('Пользователь не авторизован'));
        } else if (status === 500) {
            return Promise.reject(new Error('Ошибка сервера'));
        }
    }

    if (error.code === 'ECONNABORTED') {
        return Promise.reject(new Error('Превышен интервал ожидания для запроса'));
    }

    return Promise.reject(new Error('Неизвестная ошибка'));
};

export default BaseApiService;
